import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Mail,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Loader2,
  Settings,
  FileText,
  Users,
  BarChart3,
  Gift,
  Eye,
  Trash2,
  Plus,
  Ban,
  TrendingUp,
  Cake,
  Bell,
  CalendarDays
} from "lucide-react";
import { toast } from "sonner";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte (qui si usava date-fns).
import { formatDate, formatDateTime } from "../lib/format";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import DataStateError from "../components/DataStateError";
import { useConferma } from "../components/ConfirmProvider";

export default function EmailPage() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const conferma = useConferma();
  const [loading, setLoading] = useState(true);
  // Distingue "non c'e' niente" da "il caricamento e' fallito": senza questo
  // un guasto di rete sembrava una pagina vuota.
  const [loadError, setLoadError] = useState(false);
  
  // Dashboard stats
  const [statistics, setStatistics] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  
  // Settings
  const [emailSettings, setEmailSettings] = useState(null);
  const [birthdaySettings, setBirthdaySettings] = useState(null);
  const [signature, setSignature] = useState(null);
  
  // Notification Rules - nuovo sistema
  const [notificationRules, setNotificationRules] = useState([]);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [sendTime, setSendTime] = useState("09:00");
  
  // Templates
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [newCampaign, setNewCampaign] = useState({
    name: "",
    subject: "",
    body_html: "",
    target_filter: {}
  });
  const [campaignPreview, setCampaignPreview] = useState(null);
  
  // Birthday
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  
  // Opt-out
  const [optOutList, setOptOutList] = useState([]);
  const [clients, setClients] = useState([]);
  
  // Dialogs
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showOptOutDialog, setShowOptOutDialog] = useState(false);
  const [showTestEmailDialog, setShowTestEmailDialog] = useState(false);
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  
  // New rule form
  const [newRule, setNewRule] = useState({ days: 30, template_id: "", type: "annual" });
  
  // Test email
  const [testEmail, setTestEmail] = useState({ email: "", client_name: "Test Cliente" });
  
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoadError(false);
    setLoading(true);
    try {
      const [
        statsRes,
        logsRes,
        settingsRes,
        birthdayRes,
        signatureRes,
        templatesRes,
        campaignsRes,
        birthdaysRes,
        optOutRes,
        clientsRes,
        rulesRes
      ] = await Promise.all([
        axios.get("/api/email-notifications/statistics?period=month"),
        axios.get("/api/email/logs?limit=50"),
        axios.get("/api/email/settings"),
        axios.get("/api/email-notifications/birthday/settings"),
        axios.get("/api/email-notifications/signature"),
        axios.get("/api/email/templates"),
        axios.get("/api/email-notifications/campaigns"),
        axios.get("/api/email-notifications/birthday/upcoming?days=30"),
        axios.get("/api/email-notifications/optout"),
        axios.get("/api/clients"),
        axios.get("/api/email-notifications/rules").catch(() => ({ data: { rules: [], enabled: false, send_time: "09:00" } }))
      ]);
      
      setStatistics(statsRes.data);
      setEmailLogs(logsRes.data.logs || []);
      setEmailSettings(settingsRes.data);
      setBirthdaySettings(birthdayRes.data);
      setSignature(signatureRes.data);
      setTemplates(templatesRes.data || []);
      setCampaigns(campaignsRes.data || []);
      setUpcomingBirthdays(birthdaysRes.data || []);
      setOptOutList(optOutRes.data || []);
      setClients(clientsRes.data.items || clientsRes.data || []);
      
      // Load notification rules
      if (rulesRes.data) {
        setNotificationRules(rulesRes.data.rules || []);
        setNotificationsEnabled(rulesRes.data.enabled || false);
        setSendTime(rulesRes.data.send_time || "09:00");
      }
    } catch (err) {
      console.error("Error fetching email data:", err);
      setLoadError(true);
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  const saveEmailSettings = async () => {
    setSaving(true);
    try {
      await axios.put("/api/email/settings", emailSettings);
      toast.success("Impostazioni salvate");
    } catch (err) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const saveNotificationRules = async () => {
    setSaving(true);
    try {
      await axios.put("/api/email-notifications/rules", {
        rules: notificationRules,
        enabled: notificationsEnabled,
        send_time: sendTime
      });
      toast.success("Regole notifiche salvate");
    } catch (err) {
      toast.error("Errore nel salvataggio regole");
    } finally {
      setSaving(false);
    }
  };

  const addRule = () => {
    if (!newRule.template_id) {
      toast.error("Seleziona un template");
      return;
    }
    
    const template = templates.find(t => t.template_id === newRule.template_id);
    const rule = {
      id: `rule_${Date.now()}`,
      days: parseInt(newRule.days),
      template_id: newRule.template_id,
      template_name: template?.name || "Template",
      type: newRule.type
    };
    
    setNotificationRules([...notificationRules, rule]);
    setNewRule({ days: 30, template_id: "", type: "annual" });
    setShowRuleDialog(false);
  };

  const removeRule = (ruleId) => {
    setNotificationRules(notificationRules.filter(r => r.id !== ruleId));
  };

  const saveBirthdaySettings = async () => {
    setSaving(true);
    try {
      await axios.put("/api/email-notifications/birthday/settings", birthdaySettings);
      toast.success("Impostazioni compleanno salvate");
    } catch (err) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const saveSignature = async () => {
    setSaving(true);
    try {
      await axios.put("/api/email-notifications/signature", null, {
        params: { signature_html: signature.html }
      });
      toast.success("Firma salvata");
    } catch (err) {
      toast.error("Errore nel salvataggio firma");
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    setSending(true);
    try {
      const res = await axios.post("/api/email/test", testEmail);
      toast.success(res.data.message);
      setShowTestEmailDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio email");
    } finally {
      setSending(false);
    }
  };

  const updateTemplate = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/email/templates/${selectedTemplate.template_id}`, selectedTemplate);
      toast.success("Template aggiornato");
      setShowTemplateDialog(false);
      fetchAllData();
    } catch (err) {
      toast.error("Errore aggiornamento template");
    } finally {
      setSaving(false);
    }
  };

  const createCampaign = async () => {
    setSaving(true);
    try {
      await axios.post("/api/email-notifications/campaigns", null, {
        params: newCampaign
      });
      toast.success("Campagna creata");
      setShowCampaignDialog(false);
      setNewCampaign({ name: "", subject: "", body_html: "", target_filter: {} });
      fetchAllData();
    } catch (err) {
      toast.error("Errore creazione campagna");
    } finally {
      setSaving(false);
    }
  };

  const previewCampaign = async (campaignId) => {
    try {
      const res = await axios.post(`/api/email-notifications/campaigns/${campaignId}/preview`);
      setCampaignPreview(res.data);
      setShowPreviewDialog(true);
    } catch (err) {
      toast.error("Errore anteprima campagna");
    }
  };

  const sendCampaign = async (campaignId) => {
    if (!(await conferma({
      titolo: "Sei sicuro di voler inviare questa campagna?",
      descrizione: "L'email partirà subito a tutti i destinatari della campagna e non potrà essere richiamata.",
    }))) return;
    
    try {
      await axios.post(`/api/email-notifications/campaigns/${campaignId}/send`);
      toast.success("Invio campagna avviato");
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio campagna");
    }
  };

  const sendBirthdayEmail = async (clientId) => {
    setSending(true);
    try {
      const res = await axios.post(`/api/email-notifications/birthday/send/${clientId}`);
      toast.success(res.data.message);
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio email compleanno");
    } finally {
      setSending(false);
    }
  };

  const addOptOut = async (clientId, optOutType) => {
    try {
      await axios.post("/api/email-notifications/optout", {
        client_id: clientId,
        opt_out_type: optOutType
      });
      toast.success("Cliente aggiunto a opt-out");
      setShowOptOutDialog(false);
      fetchAllData();
    } catch (err) {
      toast.error("Errore");
    }
  };

  const removeOptOut = async (clientId) => {
    try {
      await axios.delete(`/api/email-notifications/optout/${clientId}`);
      toast.success("Cliente rimosso da opt-out");
      fetchAllData();
    } catch (err) {
      toast.error("Errore");
    }
  };

  const triggerNotifications = async () => {
    // v3.9.12 — invio email reali ai clienti: conferma esplicita
    if (!(await conferma({
      titolo: "Inviare ADESSO le email di scadenza ai clienti?",
      descrizione:
        "Partiranno email reali a tutti i clienti con polizze in scadenza secondo le regole attive. " +
      "L'invio non è annullabile una volta partito.",
    }))) return;
    setSending(true);
    try {
      await axios.post("/api/email/trigger-now");
      toast.success("Notifiche email inviate");
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      sent: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
      failed: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
      pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
      draft: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400",
      sending: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
      completed: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
    };
    const labels = {
      sent: "Inviata",
      failed: "Fallita",
      pending: "In attesa",
      draft: "Bozza",
      sending: "In corso",
      completed: "Completata"
    };
    return (
      <Badge className={styles[status] || styles.pending}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getDaysLabel = (days) => {
    if (days > 0) return `${days} giorni prima`;
    if (days < 0) return `${Math.abs(days)} giorni dopo`;
    return "Giorno stesso";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-6" data-testid="pagina-errore-caricamento">
        <DataStateError
          titolo="Non riesco a caricare le email"
          messaggio="I dati non sono andati persi: e' il collegamento che non ha risposto."
          onRetry={fetchAllData}
        />
      </div>
    );
  }

  return (
    <div data-testid="email-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground tracking-tight">
            Email
          </h1>
          <p className="text-muted-foreground">
            Gestione email automatiche, campagne e notifiche clienti
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowTestEmailDialog(true)}>
            <Send className="mr-2 h-4 w-4" />
            Invia Test
          </Button>
          <Button onClick={triggerNotifications} disabled={sending}>
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Invia Notifiche Ora
          </Button>
        </div>
      </div>

      {/* API Status */}
      <Card className={emailSettings?.api_configured ? "border-green-500" : "border-red-500"}>
        <CardContent className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            {emailSettings?.api_configured ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <XCircle className="h-5 w-5 text-red-500" />
            )}
            <div>
              <p className="font-medium">
                {emailSettings?.api_configured ? "Resend API Configurato" : "Resend API Non Configurato"}
              </p>
              <p className="text-sm text-muted-foreground">
                Mittente: {emailSettings?.sender_email || "Non configurato"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 md:grid-cols-6 gap-1">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="h-4 w-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">
            <Bell className="h-4 w-4 mr-2" />
            Notifiche
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">
            <FileText className="h-4 w-4 mr-2" />
            Template
          </TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="tab-campaigns">
            <Users className="h-4 w-4 mr-2" />
            Campagne
          </TabsTrigger>
          <TabsTrigger value="birthday" data-testid="tab-birthday">
            <Cake className="h-4 w-4 mr-2" />
            Compleanni
          </TabsTrigger>
          <TabsTrigger value="optout" data-testid="tab-optout">
            <Ban className="h-4 w-4 mr-2" />
            Opt-out
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Email Inviate (30gg)</p>
                    <p className="text-3xl font-bold">{statistics?.total_sent || 0}</p>
                  </div>
                  <Mail className="h-10 w-10 text-primary opacity-20" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Fallite</p>
                    <p className="text-3xl font-bold text-red-500">{statistics?.total_failed || 0}</p>
                  </div>
                  <XCircle className="h-10 w-10 text-red-500 opacity-20" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Tasso Successo</p>
                    <p className="text-3xl font-bold text-green-500">{statistics?.success_rate || 0}%</p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-green-500 opacity-20" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Compleanni (30gg)</p>
                    <p className="text-3xl font-bold text-orange-500">{upcomingBirthdays.length}</p>
                  </div>
                  <Gift className="h-10 w-10 text-orange-500 opacity-20" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chart */}
          {statistics?.daily_breakdown && statistics.daily_breakdown.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Email Inviate - Ultimi 7 Giorni</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ width: '100%', height: 250, minHeight: 250 }}>
                  <ResponsiveContainer width="100%" height={250} minWidth={0}>
                    <BarChart data={statistics.daily_breakdown}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                      <XAxis dataKey="day" stroke="#6b7280" />
                      <YAxis stroke="#6b7280" />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                      />
                      <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Email" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Email Logs */}
          <Card>
            <CardHeader>
              <CardTitle>Storico Email Recenti</CardTitle>
              <CardDescription>Ultime 50 email inviate</CardDescription>
            </CardHeader>
            <CardContent>
              {emailLogs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Nessuna email inviata</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Destinatario</TableHead>
                      <TableHead className="hidden md:table-cell">Oggetto</TableHead>
                      <TableHead className="hidden md:table-cell">Tipo</TableHead>
                      <TableHead>Stato</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emailLogs.map((log, idx) => (
                      <TableRow key={log.log_id || idx}>
                        <TableCell className="whitespace-nowrap">
                          {log.sent_at ? formatDateTime(log.sent_at, "-") : "-"}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{log.to_name || "-"}</p>
                            <p className="text-xs text-muted-foreground">{log.to_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell max-w-[200px] truncate">{log.subject}</TableCell>
                        <TableCell className="hidden md:table-cell">
                          <Badge variant="outline">{log.type || "notification"}</Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(log.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab - NUOVO SISTEMA */}
        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="h-5 w-5" />
                    Regole Notifiche Scadenze
                  </CardTitle>
                  <CardDescription>
                    Configura quando e quale template inviare ai clienti in base alla scadenza polizza
                  </CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label>Notifiche attive</Label>
                    <Switch
                      checked={notificationsEnabled}
                      onCheckedChange={setNotificationsEnabled}
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Orario invio */}
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <div className="flex-1">
                  <Label>Orario invio giornaliero</Label>
                  <p className="text-sm text-muted-foreground">Le email verranno inviate ogni giorno a questo orario</p>
                </div>
                <Input
                  type="time"
                  value={sendTime}
                  onChange={(e) => setSendTime(e.target.value)}
                  className="w-32"
                />
              </div>

              {/* Rules Table */}
              <div className="border rounded-lg">
                <div className="flex items-center justify-between p-4 border-b bg-muted/30">
                  <h3 className="font-medium">Regole di invio</h3>
                  <Button size="sm" onClick={() => setShowRuleDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Aggiungi Regola
                  </Button>
                </div>
                
                {notificationRules.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <CalendarDays className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nessuna regola configurata</p>
                    <p className="text-sm">Aggiungi regole per inviare email automatiche ai clienti</p>
                    <Button className="mt-4" onClick={() => setShowRuleDialog(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Aggiungi prima regola
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Giorni</TableHead>
                        <TableHead>Tipo Scadenza</TableHead>
                        <TableHead className="hidden md:table-cell">Template</TableHead>
                        <TableHead className="text-right">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {notificationRules
                        .sort((a, b) => b.days - a.days)
                        .map((rule) => (
                        <TableRow key={rule.id}>
                          <TableCell>
                            <Badge variant={rule.days > 0 ? "default" : rule.days < 0 ? "destructive" : "secondary"}>
                              {getDaysLabel(rule.days)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {rule.type === "annual" ? "Scadenza Annuale" : "Rata Semestrale"}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">{rule.template_name || rule.template_id}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeRule(rule.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Save button */}
              <div className="flex justify-end">
                <Button onClick={saveNotificationRules} disabled={saving}>
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Salva Configurazione
                </Button>
              </div>

              {/* Info box */}
              <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-2">Come funziona</h4>
                <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                  <li>• <strong>Giorni positivi</strong> (es: 30, 15, 7) = email inviate PRIMA della scadenza</li>
                  <li>• <strong>Giorni negativi</strong> (es: -3, -7) = email inviate DOPO la scadenza (sollecito)</li>
                  <li>• <strong>Zero</strong> = email inviata il giorno stesso della scadenza</li>
                  <li>• Puoi associare template diversi a giorni diversi</li>
                  <li>• I clienti in opt-out non riceveranno email</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Email al Broker */}
          <Card>
            <CardHeader>
              <CardTitle>Notifiche al Broker</CardTitle>
              <CardDescription>Ricevi un riepilogo delle scadenze via email</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Email giornaliera scadenze</Label>
                  <p className="text-sm text-muted-foreground">Ricevi ogni giorno l'elenco delle polizze in scadenza</p>
                </div>
                <Switch
                  checked={emailSettings?.enabled || false}
                  onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, enabled: checked })}
                />
              </div>
              <Button onClick={saveEmailSettings} disabled={saving} variant="outline">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Salva
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Template Email</CardTitle>
              <CardDescription>
                Personalizza i messaggi inviati ai clienti
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {templates.map((tpl) => (
                  <div 
                    key={tpl.template_id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{tpl.name}</p>
                        {tpl.is_default && <Badge variant="secondary">Predefinito</Badge>}
                        <Badge variant={tpl.active ? "default" : "outline"}>
                          {tpl.active ? "Attivo" : "Disattivo"}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {tpl.days_before} giorni prima • {tpl.subject}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setSelectedTemplate(tpl);
                          setShowTemplateDialog(true);
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Signature */}
          <Card>
            <CardHeader>
              <CardTitle>Firma Email</CardTitle>
              <CardDescription>
                Personalizza la firma che appare in fondo a tutte le email
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Codice HTML Firma</Label>
                <Textarea
                  value={signature?.html || ""}
                  onChange={(e) => setSignature({ ...signature, html: e.target.value })}
                  rows={10}
                  className="font-mono text-sm"
                  placeholder="<div>La tua firma HTML...</div>"
                />
              </div>
              <div className="flex justify-end">
                <Button onClick={saveSignature} disabled={saving}>
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Salva Firma
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Campagne Email</h3>
              <p className="text-sm text-muted-foreground">Invia comunicazioni a gruppi di clienti</p>
            </div>
            <Button onClick={() => setShowCampaignDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Nuova Campagna
            </Button>
          </div>

          {campaigns.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">Nessuna campagna creata</p>
                <Button className="mt-4" onClick={() => setShowCampaignDialog(true)}>
                  Crea la prima campagna
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {campaigns.map((campaign) => (
                <Card key={campaign.campaign_id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{campaign.name}</p>
                          {getStatusBadge(campaign.status)}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{campaign.subject}</p>
                        {campaign.status === "completed" && (
                          <p className="text-xs text-muted-foreground mt-1">
                            ✓ {campaign.sent_count} inviate • ✗ {campaign.failed_count} fallite
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => previewCampaign(campaign.campaign_id)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        {campaign.status === "draft" && (
                          <Button size="sm" onClick={() => sendCampaign(campaign.campaign_id)}>
                            <Send className="h-4 w-4 mr-1" />
                            Invia
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Birthday Tab */}
        <TabsContent value="birthday" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cake className="h-5 w-5" />
                Impostazioni Email Compleanno
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Abilita email compleanno automatiche</Label>
                  <p className="text-sm text-muted-foreground">Invia auguri automatici ai clienti</p>
                </div>
                <Switch
                  checked={birthdaySettings?.enabled || false}
                  onCheckedChange={(checked) => setBirthdaySettings({ ...birthdaySettings, enabled: checked })}
                />
              </div>
              <div>
                <Label>Oggetto Email</Label>
                <Input
                  value={birthdaySettings?.subject || ""}
                  onChange={(e) => setBirthdaySettings({ ...birthdaySettings, subject: e.target.value })}
                />
              </div>
              <div>
                <Label>Messaggio (HTML)</Label>
                <Textarea
                  value={birthdaySettings?.body_html || ""}
                  onChange={(e) => setBirthdaySettings({ ...birthdaySettings, body_html: e.target.value })}
                  rows={6}
                  placeholder="Usa {{client_name}} e {{broker_name}} come variabili"
                />
              </div>
              <Button onClick={saveBirthdaySettings} disabled={saving}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Salva Impostazioni
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Compleanni in Arrivo (30 giorni)</CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingBirthdays.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Nessun compleanno nei prossimi 30 giorni</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="hidden md:table-cell">Data</TableHead>
                      <TableHead className="hidden md:table-cell">Età</TableHead>
                      <TableHead>Giorni</TableHead>
                      <TableHead>Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {upcomingBirthdays.map((client) => (
                      <TableRow key={client.client_id}>
                        <TableCell>
                          <p className="font-medium">{client.name}</p>
                          <p className="text-xs text-muted-foreground">{client.email || "No email"}</p>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {client.next_birthday ? formatDate(client.next_birthday, "-") : "-"}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">{client.age} anni</TableCell>
                        <TableCell>
                          <Badge variant={client.days_until_birthday === 0 ? "destructive" : "outline"}>
                            {client.days_until_birthday === 0 ? "OGGI!" : `${client.days_until_birthday} gg`}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!client.email || sending}
                            onClick={() => sendBirthdayEmail(client.client_id)}
                          >
                            <Gift className="h-4 w-4 mr-1" />
                            Invia Auguri
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Opt-out Tab */}
        <TabsContent value="optout" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Gestione Opt-out</h3>
              <p className="text-sm text-muted-foreground">Clienti che hanno richiesto di non ricevere email</p>
            </div>
            <Button onClick={() => setShowOptOutDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Aggiungi Opt-out
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              {optOutList.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Nessun cliente in opt-out</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="hidden md:table-cell">Motivo</TableHead>
                      <TableHead>Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {optOutList.map((opt) => (
                      <TableRow key={opt.client_id}>
                        <TableCell>
                          <p className="font-medium">{opt.client_name || opt.client_id}</p>
                          <p className="text-xs text-muted-foreground">{opt.client_email}</p>
                        </TableCell>
                        <TableCell>
                          <Badge variant={opt.opt_out_type === "all" ? "destructive" : "secondary"}>
                            {opt.opt_out_type === "all" ? "Tutte" : opt.opt_out_type === "marketing" ? "Marketing" : "Promemoria"}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">{opt.reason || "-"}</TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeOptOut(opt.client_id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Test Email Dialog */}
      <Dialog open={showTestEmailDialog} onOpenChange={setShowTestEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invia Email di Test</DialogTitle>
            <DialogDescription>
              Verifica che la configurazione email funzioni correttamente
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email Destinatario</Label>
              <Input
                type="email"
                value={testEmail.email}
                onChange={(e) => setTestEmail({ ...testEmail, email: e.target.value })}
                placeholder="tuaemail@esempio.it"
              />
            </div>
            <div>
              <Label>Nome Cliente (per test)</Label>
              <Input
                value={testEmail.client_name}
                onChange={(e) => setTestEmail({ ...testEmail, client_name: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestEmailDialog(false)}>Annulla</Button>
            <Button onClick={sendTestEmail} disabled={sending || !testEmail.email}>
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Invia Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Edit Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Modifica Template</DialogTitle>
            <DialogDescription>
              Personalizza il contenuto dell'email
            </DialogDescription>
          </DialogHeader>
          {selectedTemplate && (
            <div className="space-y-4">
              <div>
                <Label>Nome</Label>
                <Input
                  value={selectedTemplate.name}
                  onChange={(e) => setSelectedTemplate({ ...selectedTemplate, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Oggetto</Label>
                <Input
                  value={selectedTemplate.subject}
                  onChange={(e) => setSelectedTemplate({ ...selectedTemplate, subject: e.target.value })}
                />
              </div>
              <div>
                <Label>Corpo Email (HTML)</Label>
                <Textarea
                  value={selectedTemplate.body_html}
                  onChange={(e) => setSelectedTemplate({ ...selectedTemplate, body_html: e.target.value })}
                  rows={10}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Variabili: {"{{client_name}}"}, {"{{broker_name}}"}, {"{{policies_table}}"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={selectedTemplate.active}
                  onCheckedChange={(checked) => setSelectedTemplate({ ...selectedTemplate, active: checked })}
                />
                <Label>Template Attivo</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>Annulla</Button>
            <Button onClick={updateTemplate} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Dialog */}
      <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nuova Campagna Email</DialogTitle>
            <DialogDescription>
              Crea una campagna per inviare email a gruppi di clienti
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome Campagna</Label>
              <Input
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                placeholder="es: Promozione RC Auto Marzo"
              />
            </div>
            <div>
              <Label>Oggetto Email</Label>
              <Input
                value={newCampaign.subject}
                onChange={(e) => setNewCampaign({ ...newCampaign, subject: e.target.value })}
                placeholder="es: Offerta speciale per te!"
              />
            </div>
            <div>
              <Label>Filtro Destinatari</Label>
              <Select
                value={newCampaign.target_filter?.policy_type || "all"}
                onValueChange={(value) => setNewCampaign({
                  ...newCampaign,
                  target_filter: value === "all" ? {} : { policy_type: value }
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tutti i clienti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i clienti con email</SelectItem>
                  <SelectItem value="auto">Solo clienti con polizza Auto</SelectItem>
                  <SelectItem value="casa">Solo clienti con polizza Casa</SelectItem>
                  <SelectItem value="vita">Solo clienti con polizza Vita</SelectItem>
                  <SelectItem value="salute">Solo clienti con polizza Salute</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Messaggio (HTML)</Label>
              <Textarea
                value={newCampaign.body_html}
                onChange={(e) => setNewCampaign({ ...newCampaign, body_html: e.target.value })}
                rows={8}
                placeholder="<p>Gentile {{client_name}},</p><p>Il tuo messaggio...</p>"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampaignDialog(false)}>Annulla</Button>
            <Button onClick={createCampaign} disabled={saving || !newCampaign.name || !newCampaign.subject}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Crea Campagna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Anteprima</DialogTitle>
            <DialogDescription>
              Visualizza come apparirà l'email
            </DialogDescription>
          </DialogHeader>
          {campaignPreview && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                <span className="font-semibold">Destinatari: {campaignPreview.target_count} clienti</span>
              </div>
              <p className="font-semibold">Oggetto: {campaignPreview.subject}</p>
              <div 
                className="border rounded-lg p-4 bg-white"
                dangerouslySetInnerHTML={{ __html: campaignPreview.body_html }}
              />
              {campaignPreview.sample_clients?.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Esempio destinatari:</p>
                  <div className="text-sm">
                    {campaignPreview.sample_clients.map((c, i) => (
                      <span key={i}>{c.name} ({c.email}){i < campaignPreview.sample_clients.length - 1 ? ", " : ""}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Opt-out Dialog */}
      <Dialog open={showOptOutDialog} onOpenChange={setShowOptOutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Cliente a Opt-out</DialogTitle>
            <DialogDescription>
              Seleziona un cliente che non vuole ricevere email
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Cliente</Label>
              <Select onValueChange={(clientId) => {
                const client = clients.find(c => c.client_id === clientId);
                if (client) {
                  addOptOut(clientId, "all");
                }
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clients
                    .filter(c => c.client_id && !optOutList.find(o => o.client_id === c.client_id))
                    .map((client) => (
                      <SelectItem key={client.client_id} value={client.client_id}>
                        {client.name} ({client.email || "no email"})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Rule Dialog */}
      <Dialog open={showRuleDialog} onOpenChange={setShowRuleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Regola Notifica</DialogTitle>
            <DialogDescription>
              Configura quando inviare l'email e quale template usare
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Giorni rispetto alla scadenza</Label>
              <Input
                type="number"
                value={newRule.days}
                onChange={(e) => setNewRule({ ...newRule, days: parseInt(e.target.value) || 0 })}
                placeholder="30"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Positivo = prima della scadenza, Negativo = dopo la scadenza (sollecito)
              </p>
            </div>
            <div>
              <Label>Tipo Scadenza</Label>
              <Select
                value={newRule.type}
                onValueChange={(value) => setNewRule({ ...newRule, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="annual">Scadenza Annuale</SelectItem>
                  <SelectItem value="semiannual">Rata Semestrale</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Template Email</Label>
              <Select
                value={newRule.template_id}
                onValueChange={(value) => setNewRule({ ...newRule, template_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.filter(t => t.template_id).map((tpl) => (
                    <SelectItem key={tpl.template_id} value={tpl.template_id}>
                      {tpl.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRuleDialog(false)}>Annulla</Button>
            <Button onClick={addRule} disabled={!newRule.template_id}>
              <Plus className="mr-2 h-4 w-4" />
              Aggiungi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
